import { ApiClientError, normalizeHttpError, offlineError, timeoutError } from './errors'

export interface ApiClientOptions {
  baseUrl: string
  credentials?: RequestCredentials
  timeoutMs?: number
  authHeaders?: () => HeadersInit
  mutationHeaders?: () => HeadersInit
  fetchImplementation?: typeof fetch
  online?: () => boolean
}

export interface RequestOptions extends RequestInit {
  timeoutMs?: number
}

export function createApiClient(options: ApiClientOptions) {
  const baseUrl = options.baseUrl.replace(/\/$/, '')

  return async function request<T>(path: string, init: RequestOptions = {}): Promise<T> {
    const { timeoutMs: requestTimeoutMs, ...fetchInit } = init
    const method = (init.method || 'GET').toUpperCase()
    const headers = new Headers(options.authHeaders?.())
    new Headers(init.headers).forEach((value, key) => headers.set(key, value))
    if (init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json')
    if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
      new Headers(options.mutationHeaders?.()).forEach((value, key) => headers.set(key, value))
    }
    const controller = new AbortController()
    const timeout = Math.max(1, requestTimeoutMs ?? options.timeoutMs ?? 15_000)
    const timer = globalThis.setTimeout(() => controller.abort(), timeout)
    try {
      const response = await (options.fetchImplementation || globalThis.fetch)(`${baseUrl}${path}`, {
        ...fetchInit,
        headers,
        credentials: options.credentials,
        signal: controller.signal,
      })
      const contentType = response.headers.get('content-type') || ''
      const payload: unknown = contentType.includes('application/json')
        ? await response.json()
        : await response.text()
      if (!response.ok) {
        throw normalizeHttpError(payload, response.status, response.headers.get('x-request-id') || '')
      }
      return payload as T
    } catch (error) {
      if (error instanceof ApiClientError) throw error
      if (
        (typeof DOMException !== 'undefined' && error instanceof DOMException && error.name === 'AbortError')
        || (typeof error === 'object' && error !== null && 'name' in error && error.name === 'AbortError')
      ) throw timeoutError()
      const isOnline = options.online ? options.online() : globalThis.navigator?.onLine !== false
      if (!isOnline) throw offlineError()
      throw new ApiClientError(
        'Не удалось связаться с сервером.',
        0,
        'NETWORK_ERROR',
        '',
        true,
        {},
        'request_failed',
      )
    } finally {
      globalThis.clearTimeout(timer)
    }
  }
}
